// Scene renderer — shared rendering pipeline for game and editor.
// Owns the full deferred pipeline: G-buffer, GI, lighting, volumetrics, post-process.
// Static scene geometry is baked once on scene change. Dynamic instances added per frame.

import type { GPU } from "./gpu.js";
import { mat4Invert, type Mat4 } from "./math3d.js";
import { DeferredPipeline, INSTANCE_STRIDE } from "./deferred.js";
import { VoxelGI } from "./gi.js";
import { GITrace } from "./giTrace.js";
import { LightingPass } from "./lighting.js";
import { PostProcess } from "./postprocess.js";
import { Volumetrics } from "./volumetrics.js";
import { Ocean } from "./ocean.js";
import { WaterComposite, DEFAULT_OCEAN_TWEAKS, type OceanTweaks } from "./water.js";
import { Clouds, DEFAULT_CLOUD_CONFIG, type CloudConfig } from "./clouds.js";
import { SunShadowPass } from "./sunShadow.js";
import type { SceneRenderConfig } from "./scene.js";

// Unit cube: position + normal (36 verts)
// prettier-ignore
const CUBE_VERTS = new Float32Array([
  -0.5,-0.5, 0.5,  0, 0, 1,   0.5,-0.5, 0.5,  0, 0, 1,   0.5, 0.5, 0.5,  0, 0, 1,
  -0.5,-0.5, 0.5,  0, 0, 1,   0.5, 0.5, 0.5,  0, 0, 1,  -0.5, 0.5, 0.5,  0, 0, 1,
   0.5,-0.5,-0.5,  0, 0,-1,  -0.5,-0.5,-0.5,  0, 0,-1,  -0.5, 0.5,-0.5,  0, 0,-1,
   0.5,-0.5,-0.5,  0, 0,-1,  -0.5, 0.5,-0.5,  0, 0,-1,   0.5, 0.5,-0.5,  0, 0,-1,
  -0.5, 0.5, 0.5,  0, 1, 0,   0.5, 0.5, 0.5,  0, 1, 0,   0.5, 0.5,-0.5,  0, 1, 0,
  -0.5, 0.5, 0.5,  0, 1, 0,   0.5, 0.5,-0.5,  0, 1, 0,  -0.5, 0.5,-0.5,  0, 1, 0,
  -0.5,-0.5,-0.5,  0,-1, 0,   0.5,-0.5,-0.5,  0,-1, 0,   0.5,-0.5, 0.5,  0,-1, 0,
  -0.5,-0.5,-0.5,  0,-1, 0,   0.5,-0.5, 0.5,  0,-1, 0,  -0.5,-0.5, 0.5,  0,-1, 0,
   0.5,-0.5, 0.5,  1, 0, 0,   0.5,-0.5,-0.5,  1, 0, 0,   0.5, 0.5,-0.5,  1, 0, 0,
   0.5,-0.5, 0.5,  1, 0, 0,   0.5, 0.5,-0.5,  1, 0, 0,   0.5, 0.5, 0.5,  1, 0, 0,
  -0.5,-0.5,-0.5, -1, 0, 0,  -0.5,-0.5, 0.5, -1, 0, 0,  -0.5, 0.5, 0.5, -1, 0, 0,
  -0.5,-0.5,-0.5, -1, 0, 0,  -0.5, 0.5, 0.5, -1, 0, 0,  -0.5, 0.5,-0.5, -1, 0, 0,
]);

const MAX_INSTANCES = 512;
const FLOATS_PER_INST = INSTANCE_STRIDE / 4;

export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16 & 0xff) / 255, (n >> 8 & 0xff) / 255, (n & 0xff) / 255];
}

export type RenderConfig = {
  ambient: number;
  giStrength: number;
  reflStrength: number;
  fogColor: [number, number, number];
  fogDensity: number;
  sunDir: [number, number, number];
  volDensity: number;
  volStrength: number;
  exposure: number;
  bloomStrength: number;
  bloomThreshold: number;
  aoStrength: number;
  reflTint: number;
};

export class SceneRenderer {
  readonly deferred: DeferredPipeline;
  readonly gi: VoxelGI;
  readonly giTrace: GITrace;
  readonly sunShadow: SunShadowPass;
  readonly lighting: LightingPass;
  readonly vol: Volumetrics;
  readonly post: PostProcess;
  readonly ocean: Ocean;
  readonly clouds: Clouds;
  private waterComposite: WaterComposite | null = null;

  private cubeVertBuffer: GPUBuffer;
  private instanceBuffer: GPUBuffer;
  private instData = new Float32Array(MAX_INSTANCES * FLOATS_PER_INST);
  private instCount = 0;
  private meshVertBuffer: GPUBuffer | null = null;
  private meshColorBuffer: GPUBuffer | null = null;
  private meshVertCount = 0;
  private startTime = performance.now() / 1000;
  private sceneRender: SceneRenderConfig;

  // Pre-baked static instance data (only rebuilt on scene change)
  private staticInstData = new Float32Array(MAX_INSTANCES * FLOATS_PER_INST);
  private staticInstCount = 0;

  // Water refraction: copies of opaque scene before ocean overlay
  private opaqueSceneCopy: GPUTexture | null = null;
  private opaqueDepthCopy: GPUTexture | null = null;

  cfg: RenderConfig;
  oceanTweaks: OceanTweaks = { ...DEFAULT_OCEAN_TWEAKS };
  cloudConfig: CloudConfig;
  triCount = 0;

  constructor(private gpu: GPU, sceneRender: SceneRenderConfig) {
    const { device } = gpu;
    this.sceneRender = sceneRender;

    this.deferred = new DeferredPipeline(gpu);
    this.gi = new VoxelGI(gpu);
    this.gi.configure({ min: sceneRender.voxelBounds.min, max: sceneRender.voxelBounds.max });
    if (sceneRender.ocean) this.gi.mediumCeil = 0;
    this.giTrace = new GITrace(gpu, this.deferred.gBuffer, gpu.depthTexture, this.gi.litVolume, this.gi.volumeSampler);
    this.sunShadow = new SunShadowPass(gpu);
    this.lighting = new LightingPass(gpu, this.deferred.gBuffer, gpu.depthTexture, this.gi.litVolume, this.gi.volumeSampler, this.giTrace.output, this.sunShadow.depthTexture, "rgba16float");

    this.cfg = {
      ambient: 0.075,
      giStrength: sceneRender.giStrength,
      reflStrength: sceneRender.reflStrength,
      fogColor: [...sceneRender.fog.color],
      fogDensity: sceneRender.fog.density,
      sunDir: [...sceneRender.sunDir],
      volDensity: 0.03,
      volStrength: 2.15,
      exposure: 1.0,
      bloomStrength: 0.5,
      bloomThreshold: 0.9,
      aoStrength: 1.51,
      reflTint: 0.75,
    };
    this.vol = new Volumetrics(gpu, this.gi.litVolume, this.gi.volumeSampler, gpu.depthTexture, {
      density: this.cfg.volDensity, strength: this.cfg.volStrength, maxDist: 50,
    });
    this.ocean = new Ocean(gpu);
    this.clouds = new Clouds(gpu, gpu.depthTexture);
    this.cloudConfig = this.clouds.config;

    // Water refraction resources (only when ocean is enabled)
    if (sceneRender.ocean) {
      const { canvas } = gpu;
      // Copy of opaque lit HDR scene before ocean overlay
      this.opaqueSceneCopy = device.createTexture({
        size: [canvas.width, canvas.height], format: "rgba16float",
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });
      // Copy of depth buffer before ocean overlay (for water depth calculation)
      this.opaqueDepthCopy = device.createTexture({
        size: [canvas.width, canvas.height], format: "depth24plus",
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });
    } else {
      this.waterComposite = null;
    }

    this.post = new PostProcess(gpu, this.cfg, undefined, this.vol.output, this.clouds.output, gpu.depthTexture);

    // WaterComposite must be created after PostProcess (needs hdrTarget as write target)
    if (sceneRender.ocean && this.opaqueSceneCopy && this.opaqueDepthCopy) {
      this.waterComposite = new WaterComposite(
        gpu, this.deferred.gBuffer, gpu.depthTexture,
        this.opaqueSceneCopy, this.opaqueDepthCopy,
        this.gi.litVolume, this.gi.volumeSampler,
        this.giTrace.output, "rgba16float",
      );
    }

    // Vertex buffers
    this.cubeVertBuffer = device.createBuffer({ size: CUBE_VERTS.byteLength, usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST });
    device.queue.writeBuffer(this.cubeVertBuffer, 0, CUBE_VERTS);
    this.instanceBuffer = device.createBuffer({ size: MAX_INSTANCES * INSTANCE_STRIDE, usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST });

    // Mesh geometry (ramps etc.)
    this.uploadMesh(sceneRender);

    // Bake static instances
    this.bakeStaticInstances();
  }

  /** Update scene data without recreating the pipeline (preserves ocean animation).
   *  Re-bakes static instance data — call only when brushes/lights change, not per frame. */
  updateScene(sceneRender: SceneRenderConfig): void {
    this.sceneRender = sceneRender;
    this.gi.configure({ min: sceneRender.voxelBounds.min, max: sceneRender.voxelBounds.max });
    this.sunShadow.markDirty();
    this.uploadMesh(sceneRender);
    this.bakeStaticInstances();
  }

  /** Bake scene boxes into static instance data (hex→rgb parsed once, not per frame) */
  private bakeStaticInstances(): void {
    this.staticInstCount = 0;
    for (const b of this.sceneRender.sceneBoxes) {
      if (this.staticInstCount >= MAX_INSTANCES) break;
      const [r, g, bb] = hexToRgb(b.color);
      const off = this.staticInstCount * FLOATS_PER_INST;
      this.staticInstData[off] = b.x; this.staticInstData[off+1] = b.y; this.staticInstData[off+2] = b.z;
      this.staticInstData[off+3] = b.w; this.staticInstData[off+4] = b.h; this.staticInstData[off+5] = b.d;
      this.staticInstData[off+6] = r; this.staticInstData[off+7] = g; this.staticInstData[off+8] = bb;
      this.staticInstData[off+9] = b.emissive ?? 0;
      this.staticInstData[off+10] = b.roughness ?? 0.9;
      this.staticInstCount++;
    }
  }

  private uploadMesh(sceneRender: SceneRenderConfig): void {
    if (sceneRender.meshVertices && sceneRender.meshVertices.length > 0) {
      const { device } = this.gpu;
      // Destroy old buffers to avoid GPU memory leaks
      this.meshVertBuffer?.destroy();
      this.meshColorBuffer?.destroy();
      const verts = sceneRender.meshVertices;
      this.meshVertCount = verts.length / 9;
      const posNormData = new Float32Array(this.meshVertCount * 6);
      const colorData = new Float32Array(this.meshVertCount * 3);
      for (let i = 0; i < this.meshVertCount; i++) {
        const s = i * 9;
        posNormData[i*6] = verts[s]; posNormData[i*6+1] = verts[s+1]; posNormData[i*6+2] = verts[s+2];
        posNormData[i*6+3] = verts[s+3]; posNormData[i*6+4] = verts[s+4]; posNormData[i*6+5] = verts[s+5];
        colorData[i*3] = verts[s+6]; colorData[i*3+1] = verts[s+7]; colorData[i*3+2] = verts[s+8];
      }
      this.meshVertBuffer = device.createBuffer({ size: posNormData.byteLength, usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST });
      device.queue.writeBuffer(this.meshVertBuffer, 0, posNormData);
      this.meshColorBuffer = device.createBuffer({ size: colorData.byteLength, usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST });
      device.queue.writeBuffer(this.meshColorBuffer, 0, colorData);
    } else {
      this.meshVertBuffer = null;
      this.meshColorBuffer = null;
      this.meshVertCount = 0;
    }
  }

  /** Start a new frame — copies pre-baked static instances. Add dynamic instances after this. */
  beginInstances(): void {
    // Copy static data (no hex parsing, no per-box work)
    this.instData.set(this.staticInstData.subarray(0, this.staticInstCount * FLOATS_PER_INST));
    this.instCount = this.staticInstCount;
  }

  /** Add a dynamic instance for this frame (entities, selection highlights, etc.) */
  addInstance(x: number, y: number, z: number, w: number, h: number, d: number, color: string, emissive = 0, roughness = 0.9): void {
    if (this.instCount >= MAX_INSTANCES) return;
    const [r, g, b] = hexToRgb(color);
    const off = this.instCount * FLOATS_PER_INST;
    this.instData[off] = x; this.instData[off+1] = y; this.instData[off+2] = z;
    this.instData[off+3] = w; this.instData[off+4] = h; this.instData[off+5] = d;
    this.instData[off+6] = r; this.instData[off+7] = g; this.instData[off+8] = b;
    this.instData[off+9] = emissive;
    this.instData[off+10] = roughness;
    this.instCount++;
  }

  /** Add a raw RGB instance (avoids hex parsing — use for per-frame dynamic instances) */
  addInstanceRgb(x: number, y: number, z: number, w: number, h: number, d: number, r: number, g: number, b: number, emissive = 0, roughness = 0.9): void {
    if (this.instCount >= MAX_INSTANCES) return;
    const off = this.instCount * FLOATS_PER_INST;
    this.instData[off] = x; this.instData[off+1] = y; this.instData[off+2] = z;
    this.instData[off+3] = w; this.instData[off+4] = h; this.instData[off+5] = d;
    this.instData[off+6] = r; this.instData[off+7] = g; this.instData[off+8] = b;
    this.instData[off+9] = emissive;
    this.instData[off+10] = roughness;
    this.instCount++;
  }

  /** Render a full frame */
  renderFrame(viewProj: Mat4, camPos: [number, number, number]): void {
    const { device } = this.gpu;
    const c = this.cfg;
    const hasWater = this.sceneRender.ocean && this.waterComposite;

    device.queue.writeBuffer(this.instanceBuffer, 0, this.instData, 0, this.instCount * FLOATS_PER_INST);

    const encoder = device.createCommandEncoder();

    // --- 1. Opaque G-buffer (no ocean) ---
    this.deferred.setViewProj(viewProj);
    const geoPass = this.deferred.beginPass(encoder);
    var tris = 0;

    if (this.instCount > 0) {
      geoPass.setPipeline(this.deferred.instancedPipeline);
      geoPass.setBindGroup(0, this.deferred.bindGroup);
      geoPass.setVertexBuffer(0, this.cubeVertBuffer);
      geoPass.setVertexBuffer(1, this.instanceBuffer);
      geoPass.draw(36, this.instCount);
      tris += 12 * this.instCount;
    }

    if (this.meshVertBuffer && this.meshColorBuffer && this.meshVertCount > 0) {
      geoPass.setPipeline(this.deferred.staticPipeline);
      geoPass.setBindGroup(0, this.deferred.bindGroup);
      geoPass.setVertexBuffer(0, this.meshVertBuffer);
      geoPass.setVertexBuffer(1, this.meshColorBuffer);
      geoPass.draw(this.meshVertCount);
      tris += this.meshVertCount / 3;
    }

    // If no water pipeline, draw ocean in the opaque pass (legacy path)
    if (!hasWater && this.sceneRender.ocean) {
      this.ocean.draw(geoPass, viewProj, performance.now() / 1000 - this.startTime);
      tris += this.ocean.triangleCount;
    }

    geoPass.end();

    // --- 2. Voxel GI ---
    this.gi.beginFrame();
    for (const vb of this.sceneRender.voxelBoxes) {
      this.gi.addBox(vb.x, vb.y, vb.z, vb.w, vb.h, vb.d, vb.r, vb.g, vb.b, vb.emissive ?? 0);
    }
    this.gi.compute(encoder, [0.3, 1.0, 0.5], this.sceneRender.lights);

    // --- 3. Half-res ambient color + AO ---
    const vb = this.sceneRender.voxelBounds;
    const invViewProj = mat4Invert(viewProj);
    this.giTrace.compute(encoder, invViewProj, vb.min, vb.max, camPos);

    // --- 3.5 Sun shadow map ---
    this.sunShadow.render(encoder, c.sunDir, vb,
      this.cubeVertBuffer, this.instanceBuffer, this.instCount,
      this.meshVertBuffer, this.meshVertCount);

    // --- 4. Lighting (opaque scene → hdrTarget) ---
    const activeLights = this.sceneRender.lights;

    this.lighting.render(encoder, {
      invViewProj,
      camPos,
      ambient: c.ambient,
      lightDir: c.sunDir,
      giStrength: c.giStrength,
      voxelMin: vb.min,
      reflStrength: c.reflStrength,
      voxelMax: vb.max,
      fogColor: c.fogColor,
      fogDensity: c.fogDensity,
      aoStrength: c.aoStrength,
      reflTint: c.reflTint,
      lightViewProj: this.sunShadow.lightViewProj,
      pointLights: activeLights,
    }, this.post.hdrTarget.createView());

    if (hasWater) {
      // --- 5. Copy opaque scene + depth buffer ---
      encoder.copyTextureToTexture(
        { texture: this.post.hdrTarget },
        { texture: this.opaqueSceneCopy! },
        [this.gpu.canvas.width, this.gpu.canvas.height],
      );
      encoder.copyTextureToTexture(
        { texture: this.gpu.depthTexture },
        { texture: this.opaqueDepthCopy! },
        [this.gpu.canvas.width, this.gpu.canvas.height],
      );

      // --- 6. Ocean G-buffer overlay (load existing, depth test against opaque) ---
      const oceanPass = this.deferred.beginLoadPass(encoder);
      this.ocean.draw(oceanPass, viewProj, performance.now() / 1000 - this.startTime);
      oceanPass.end();
      tris += this.ocean.triangleCount;

      // --- 7. Water composite (refraction + absorption → hdrTarget) ---
      this.waterComposite!.render(encoder, {
        invViewProj,
        camPos,
        lightDir: c.sunDir,
        giStrength: c.giStrength,
        reflStrength: c.reflStrength,
        voxelMin: vb.min,
        voxelMax: vb.max,
        fogColor: c.fogColor,
        fogDensity: c.fogDensity,
        tweaks: this.oceanTweaks,
      }, this.post.hdrTarget.createView());
    }

    // --- 8. Volumetrics ---
    const [sr, sg, sh] = c.sunDir;
    const sunLen = Math.sqrt(sr*sr + sg*sg + sh*sh);
    this.vol.compute(encoder, {
      viewProj,
      camPos,
      sunDir: sunLen > 0.001 ? [sr/sunLen, sg/sunLen, sh/sunLen] : [0, 1, 0],
      sunIntensity: sunLen,
      voxelMin: vb.min,
      voxelMax: vb.max,
      pointLights: activeLights,
    });

    // --- 9. Clouds (quarter-res) ---
    this.clouds.compute(encoder, {
      viewProj,
      camPos,
      sunDir: c.sunDir,
      sunIntensity: sunLen,
      time: performance.now() / 1000,
      fogColor: c.fogColor,
    });

    // --- 10. Post-processing ---
    this.post.render(encoder);

    this.triCount = tris;
    device.queue.submit([encoder.finish()]);
  }
}
